/**
 * The colours of a theme. Every colour in the app comes from here: components use `color.<name>`,
 * so a theme setting can change all of them at once.
 */
export type Theme = {
    /** Behind everything. */
    background: string;
    /** Fields and quiet areas, like the search box. */
    surface: string;
    /** Panels that open over or next to the sheet. */
    panel: string;
    text: string;
    textSoft: string;
    textMuted: string;
    /** Placeholders and disabled controls. */
    textFaint: string;
    border: string;
    borderStrong: string;
    accent: string;
    accentHover: string;
    /** Behind selected items and badges. */
    accentSoft: string;
    accentSofter: string;
    /** Text on an accent background. */
    onAccent: string;
    /** Chords on a chord sheet. */
    chord: string;
    /** The "this setlist only" tag. */
    setlistOnly: string;
    setlistOnlySoft: string;
    danger: string;
    dangerSoft: string;
    success: string;
    successSoft: string;
    /** Covers the page behind a dialog or slide-over. */
    scrim: string;
    shadow: string;
};

export const lightTheme: Theme = {
    background: "#FFFFFF",
    surface: "#F3F4F6",
    panel: "#FAFBFC",
    text: "#15171C",
    textSoft: "#3D4250",
    textMuted: "#5B6170",
    textFaint: "#8A90A0",
    border: "#E3E5EA",
    borderStrong: "#C9CDD6",
    accent: "#2F49C4",
    accentHover: "#22379A",
    accentSoft: "#EBEEFB",
    accentSofter: "#F5F7FE",
    onAccent: "#FFFFFF",
    chord: "#2F49C4",
    setlistOnly: "#8A3F00",
    setlistOnlySoft: "#FCEEDF",
    danger: "#9B2C2C",
    dangerSoft: "#FDF3F3",
    success: "#1E6B3A",
    successSoft: "#E4F3E8",
    scrim: "rgba(21, 23, 28, 0.4)",
    shadow: "rgba(21, 23, 28, 0.16)",
};

export const darkTheme: Theme = {
    background: "#121317",
    surface: "#1C1E24",
    panel: "#17191E",
    text: "#ECEEF2",
    textSoft: "#C5CAD3",
    textMuted: "#A3A9B6",
    textFaint: "#6E7482",
    border: "#2A2D35",
    borderStrong: "#3A3E48",
    accent: "#8EA2FF",
    accentHover: "#B0BEFF",
    accentSoft: "#232A4A",
    accentSofter: "#1B2038",
    onAccent: "#0E1330",
    chord: "#9FB0FF",
    setlistOnly: "#F3B27A",
    setlistOnlySoft: "#3A2614",
    danger: "#FF9A9A",
    dangerSoft: "#3A1D1D",
    success: "#8FD6A6",
    successSoft: "#16301F",
    scrim: "rgba(0, 0, 0, 0.55)",
    shadow: "rgba(0, 0, 0, 0.5)",
};

/** Dark with a black background, for dark stages and screens that save power on black. */
export const blackTheme: Theme = {
    ...darkTheme,
    background: "#000000",
    surface: "#141518",
    panel: "#0A0B0D",
    border: "#24262C",
    borderStrong: "#34373F",
};
